/**
 * <SendToGameSection> + <SendToGameButton> — reusable delivery widget.
 *
 * Used in two places:
 *   - /builder page: appended at the bottom; sends a freshly built pal
 *     to the user's game character via `POST /api/pal/deliver`.
 *   - PalGame BoxTab card: an icon button on each owned pal; sends the
 *     existing `user_pals` row via `POST /api/pal/deliver-existing`
 *     (consume model — pal is removed from website Box on success).
 *
 * Replaces the standalone `/deliver` and `/deliver-box` routes per
 * user request (2026-06-13) so delivery lives inside the existing
 * Builder and Pal Game surfaces.
 */
import { useEffect, useState } from "react";
import { Send, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { MAX_LEVEL, MAX_LEVEL_MODDED } from "@/lib/builderPresets";
import { ServerPicker, ConfirmDeliveryModal, tierLabel } from "@/components/deliver/DeliverShared";
import { GiftAutocomplete } from "@/components/deliver/GiftAutocomplete";
import { useBuilderPricing, passiveCostFromPricing, quoteDeliveryCost, palTierName } from "@/lib/useBuilderPricing";

// Sum of per-tier passive costs + per-active-skill cost + level surcharge.
// Reads admin-configurable prices from `useBuilderPricing()` so /admin/builder-pricing
// edits take effect on the next render. Passives accept either {rank} (Builder)
// or {tier} (Box) shape.
export function totalPassiveCost(passives, level = 1, pricing, activeSkillCount = 0, isLucky = false, palName = null) {
  return quoteDeliveryCost({ passives, level, activeSkillCount, isLucky, pricing, palName });
}

/** Bottom-of-builder Send section. Drops a `<section>` directly into
 *  the existing Builder layout — preserves the rest of the page look.
 *  The Builder owns the `server` selection (see DeliveryTargetPicker
 *  at the top of /builder); we just consume it here. */
export function BuilderSendSection({
  pal, level, ivs, souls, passives, actives, isLucky, isBoss, isAwakened, nickname, rank, trust, workSuits,
  server, servers,
}) {
  const { user, refreshProfile } = useAuth();
  const toast = useToast();
  const pricing = useBuilderPricing();
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  // Optional Discord handle of the recipient. Empty string = deliver to
  // the sender (default). Non-empty = "gift a pal to a friend" flow.
  const [giftTo, setGiftTo] = useState("");

  const activeSkillCount = (actives || []).length;
  const cost = totalPassiveCost(passives, level, pricing, activeSkillCount, isLucky, pal?.name);
  const speciesTier = palTierName(pal?.name);
  const balance = user?.points || 0;
  const tooPoor = balance < cost;
  // Modded servers raise the level cap to MAX_LEVEL_MODDED; vanilla is MAX_LEVEL.
  // We block sending when the build's level exceeds the chosen server's cap.
  // Read the shared constants — restating the numbers here is how this drifted
  // to 65 and started blocking legitimate Lv 80 vanilla deliveries.
  const targetServer = (servers || []).find((s) => s.name === server);
  const serverMaxLevel = targetServer?.modded ? MAX_LEVEL_MODDED : MAX_LEVEL;
  const levelTooHigh = !!server && level > serverMaxLevel;
  const canSend = pal && server && !tooPoor && !sending && !levelTooHigh;

  const doSend = async () => {
    setSending(true);
    try {
      const params = new URLSearchParams({
        server,
        character_id: pal.name,
        level: String(level),
        passives: JSON.stringify((passives || []).map((p) => p.name)),
        // Active (combat) skills — backend resolves display names to
        // PalDefender WazaIDs and ships them in ActiveSkills (max 3).
        active_skills: JSON.stringify((actives || []).map((a) => a.name)),
        talents: JSON.stringify([ivs.hp, ivs.atk, ivs.def]),
        // Condenser star (0-4) — does NOT control the Lucky symbol any more.
        rank: String(rank || 0),
        rank_hp: String(souls.hp), rank_attack: String(souls.atk),
        rank_defence: String(souls.def), rank_craft_speed: String(souls.craft || 0),
        // Lucky and trust are explicit fields — backend uses these
        // for the PalDefender `Shiny` flag and `PartnerSkillLevel`.
        is_lucky: String(!!isLucky),
        // Alpha/Boss — when set, backend swaps to the BOSS_ PalID so the
        // pal lands as an alpha/boss in the player's inventory.
        is_boss: String(!!isBoss),
        // Awakened — PalDefender `IsAwakening` flag (1.0 awakening aura).
        awakened: String(!!isAwakened),
        trust: String(trust ?? 0),
        // ExtraWorkSuitabilities — 0-10 per work type (updated PalDefender
        // template range). Backend clamps and strips unknown keys via
        // `_normalize_work_suits`.
        work_suits: JSON.stringify(workSuits || {}),
        ...(giftTo.trim() ? { gift_to: giftTo.trim() } : {}),
        ...(nickname?.trim() ? { nick_name: nickname.trim() } : {}),
      });
      const { data } = await api.post(`/pal/deliver?${params}`);
      const who = giftTo.trim() ? ` to @${giftTo.trim()}` : "";
      toast.success(`Sent ${data.pal} (Lv ${data.level})${who} — ${data.cost} coins.`);
      setConfirm(false);
      setGiftTo("");
      if (refreshProfile) await refreshProfile();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSending(false); }
  };

  if (!pal) return null;

  return (
    <section className="glass-card p-5 mt-6 fade-up" data-testid="builder-send-section">
      <h3 className="text-lg glow-teal mb-1 flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
        <Send size={16} /> Send to Game
      </h3>
      <p className="text-xs text-[var(--muted)] mb-3">
        Materialize this build directly into your in-game character&apos;s box. Coins are deducted only after the spawn succeeds.
      </p>

      <div className="glass-card border-amber-400/30 bg-amber-500/5 p-3 mb-4 flex items-start gap-3" data-testid="builder-send-online">
        <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-[var(--muted)] leading-relaxed">
          <span className="text-amber-200 font-semibold">You must be logged into the game</span> on the chosen server when you press Send — the pal lands directly in your inventory via RCON. If you&apos;re offline the give-pal command fails and your coins aren&apos;t charged.
        </p>
      </div>

      <div
        className="flex items-center justify-between gap-3 text-xs mb-4 px-3 py-2 rounded-md bg-black/20 border border-white/5"
        data-testid="builder-send-target-summary"
      >
        <span className="text-[var(--muted)]">Target</span>
        {server ? (
          <span className="text-[var(--bone)] font-semibold truncate">
            {server}
            {targetServer?.modded && (
              <span className="ml-2 text-[9px] font-mono px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-500/10 text-amber-300">MOD</span>
            )}
          </span>
        ) : (
          <span className="text-amber-300 italic">Pick a Delivery Target at the top of the page.</span>
        )}
      </div>

      {/* Per-passive price breakdown */}
      {(passives || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1.5">Passive Costs</div>
          <ul className="text-xs text-[var(--bone)] grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {passives.map((p) => (
              <li key={p.name} className="flex justify-between" data-testid={`builder-send-passive-cost-${(p.asset || p.name).replace(/[^a-z0-9]/gi, "_")}`}>
                <span className="truncate">{p.name} <span className="text-[var(--muted)]">· {tierLabel(p.rank ?? 0)}</span></span>
                <span className="tabular-nums">{passiveCostFromPricing(p.rank ?? 0, pricing).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Active-skill breakdown — shown only when the user picked at least one. */}
      {activeSkillCount > 0 && (
        <div className="mt-3 flex items-center justify-between text-xs text-sky-200/90"
             data-testid="builder-send-active-skill-cost">
          <span>Active skills (×{activeSkillCount})</span>
          <span className="tabular-nums">+{((Number(pricing.active_skill_cost) || 0) * activeSkillCount).toLocaleString()}</span>
        </div>
      )}

      {/* High-level surcharge breakdown — only shown when applicable. */}
      {level > (Number(pricing.high_level_threshold) || 65) && (
        <div className="mt-3 flex items-center justify-between text-xs text-amber-200/90"
             data-testid="builder-send-level-surcharge">
          <span>Modded level surcharge (Lv {level})</span>
          <span className="tabular-nums">+{(Number(pricing.high_level_surcharge) || 0).toLocaleString()}</span>
        </div>
      )}

      {/* Base delivery cost — the starting point every line item adds to. */}
      <div className="mt-3 flex items-center justify-between text-xs text-[var(--muted)]"
           data-testid="builder-send-base-cost">
        <span>Base delivery cost</span>
        <span className="tabular-nums">{(Number(pricing.min_delivery_cost) || 0).toLocaleString()}</span>
      </div>

      {/* Species tax breakdown — World Tree pals pay the 1.0 tax AND the
          World Tree tax; other Palworld 1.0 pals pay the 1.0 tax only. */}
      {speciesTier && (
        <div className="mt-3 space-y-1" data-testid="builder-send-species-tax">
          <div className="flex items-center justify-between text-xs text-emerald-200/90">
            <span>Palworld 1.0 pal tax ({pal?.name})</span>
            <span className="tabular-nums">+{(Number(pricing.new_pal_min_cost) || 0).toLocaleString()}</span>
          </div>
          {speciesTier === "world_tree" && (
            <div className="flex items-center justify-between text-xs text-violet-200/90"
                 data-testid="builder-send-world-tree-tax">
              <span>World Tree pal tax</span>
              <span className="tabular-nums">+{(Number(pricing.world_tree_min_cost) || 0).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      {/* Lucky/shiny variant fee. */}
      {isLucky && (
        <div className="mt-3 flex items-center justify-between text-xs text-yellow-200/90"
             data-testid="builder-send-lucky-cost">
          <span>Lucky (shiny) variant</span>
          <span className="tabular-nums">+{(Number(pricing.lucky_variant_cost) || 0).toLocaleString()}</span>
        </div>
      )}

      {/* Hard block when build level > target server's cap. */}
      {levelTooHigh && (
        <div className="mt-3 glass-card border-red-400/40 bg-red-500/5 p-3 text-xs text-red-200"
             data-testid="builder-send-level-blocked">
          This Lv {level} build can&apos;t be delivered to <span className="font-semibold">{server}</span> (caps at Lv {serverMaxLevel}). Pick a modded server, or lower the level.
        </div>
      )}

      {/* Gift-to-a-friend — optional. When filled the pal is delivered
          into the recipient's inventory instead of yours. You still pay. */}
      <div
        className="mt-4 p-3 rounded-md bg-black/20 border border-white/5"
        data-testid="builder-send-gift"
      >
        <label
          className="text-[10px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-1.5 mb-1.5"
          htmlFor="builder-gift-to-input"
        >
          🎁 Gift to (optional)
        </label>
        <GiftAutocomplete
          value={giftTo}
          onChange={setGiftTo}
          placeholder="Discord username (e.g. friend, no #tag)"
          testIdPrefix="builder-gift-to"
        />
        {giftTo.trim() ? (
          <p
            className="text-[10px] text-fuchsia-300 mt-1 leading-snug"
            data-testid="builder-gift-to-active"
          >
            Pal will land in <span className="font-semibold">@{giftTo.trim()}</span>&apos;s inventory on {server || "the chosen server"}. They must already be /link&apos;d to that server.
            <button type="button" onClick={() => setGiftTo("")} className="ml-2 underline hover:no-underline">clear</button>
          </p>
        ) : (
          <p className="text-[10px] text-[var(--muted)] mt-1 leading-snug">
            Leave blank to send to your own character.
          </p>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-white/5 mt-4 pt-3">
        <div className="text-xs text-[var(--muted)]">
          Your balance: <span className="text-[var(--bone)]">{balance.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl glow-gold flex items-center gap-1.5" data-testid="builder-send-cost">
            <ChilletCoinIcon size={20} /> {cost.toLocaleString()}
          </span>
          <button
            onClick={() => setConfirm(true)}
            disabled={!canSend}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="builder-send-btn"
            title={!server ? "Pick a server first" : levelTooHigh ? `Lv ${level} too high for ${server} (cap ${serverMaxLevel})` : tooPoor ? `Need ${(cost - balance).toLocaleString()} more coins` : "Send to game"}
          >
            <Send size={14} /> Send to Game
          </button>
        </div>
      </div>

      {confirm && (
        <ConfirmDeliveryModal
          title={giftTo.trim() ? "Gift Pal to Friend" : "Send Pal to Game"}
          line1={
            giftTo.trim()
              ? `Gift Lv ${level} ${pal.name}${nickname ? ` "${nickname}"` : ""} to @${giftTo.trim()} on ${server}?`
              : `Send Lv ${level} ${pal.name}${nickname ? ` "${nickname}"` : ""} to ${server}?`
          }
          line2={`${(passives || []).length} passive${(passives || []).length === 1 ? "" : "s"} · ${cost.toLocaleString()} coins`}
          confirming={sending}
          onCancel={() => setConfirm(false)}
          onConfirm={doSend}
        />
      )}
    </section>
  );
}

/** Per-card "Send to Game" button used by PalGame BoxTab. Renders an
 *  inline icon button; on click opens a modal with server picker +
 *  confirm. Calls POST /api/pal/deliver-existing (consume model). */
export function SendFromBoxButton({ pal, onSuccess }) {
  const { user, refreshProfile } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [server, setServer] = useState("");
  const [servers, setServers] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/servers").then((r) => {
      setServers(r.data.servers || []);
      if ((r.data.servers || []).length === 1) setServer(r.data.servers[0].name);
    }).catch(() => {});
  }, [open]);

  const cost = pal?.cost ?? 0;
  const balance = user?.points || 0;
  const tooPoor = balance < cost;

  const doSend = async () => {
    setSending(true);
    try {
      const params = new URLSearchParams({ server, pal_id: String(pal.pal_id) });
      const { data } = await api.post(`/pal/deliver-existing?${params}`);
      toast.success(`Sent ${data.pal} (Lv ${data.level}) to ${data.server} — ${data.cost} coins.`);
      setConfirm(false); setOpen(false);
      if (refreshProfile) await refreshProfile();
      onSuccess?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="absolute bottom-1.5 right-1.5 text-[10px] px-2 py-1 rounded bg-[var(--teal-soft)] border border-[var(--teal)]/40 text-[var(--teal)] hover:bg-[var(--teal)] hover:text-black transition-colors flex items-center gap-1 z-10"
        data-testid={`box-send-btn-${pal.pal_id ?? pal.pal_name}`}
        title="Send this pal to your game server"
      >
        <Send size={10} /> Send
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          data-testid="box-send-modal"
        >
          <div className="glass-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl glow-teal mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Send {pal.pal_name} to Game</h3>
            <p className="text-xs text-[var(--muted)] mb-4">
              The pal is consumed — it will be removed from your website Box after a successful delivery.
            </p>

            <ServerPicker servers={servers} value={server} onChange={setServer} />

            <div className="flex items-center justify-between border-t border-white/5 mt-4 pt-3 mb-3">
              <span className="text-xs text-[var(--muted)]">Your balance: {balance.toLocaleString()}</span>
              <span className="text-xl glow-gold flex items-center gap-1.5">
                <ChilletCoinIcon size={16} /> {cost.toLocaleString()}
              </span>
            </div>
            {tooPoor && (
              <p className="text-[10px] text-red-400 mb-3">Need {(cost - balance).toLocaleString()} more coins.</p>
            )}

            <div className="flex gap-2 justify-end">
              <button onClick={() => setOpen(false)} className="btn btn-ghost" data-testid="box-send-cancel">Cancel</button>
              <button
                onClick={() => setConfirm(true)}
                disabled={!server || tooPoor || sending}
                className="btn btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="box-send-confirm"
              >
                <Send size={14} /> Send & Consume
              </button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmDeliveryModal
          title="Send Pal to Game"
          line1={`Send your Lv ${pal.level} ${pal.pal_name} to ${server}?`}
          line2={`${(pal.passives || []).length} passive${(pal.passives || []).length === 1 ? "" : "s"} · ${cost.toLocaleString()} coins · pal will be removed from your website Box`}
          confirmLabel="Send & Consume"
          confirming={sending}
          onCancel={() => setConfirm(false)}
          onConfirm={doSend}
          danger
        />
      )}
    </>
  );
}
